/*
 * Self-signup fallback (Feb-17). This exists because the Emergent
 * Google OAuth flow was returning S3 AccessDenied XML for some new
 * production users. A working email+password path lets them onboard
 * immediately without depending on the OAuth provider.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API, setSessionToken } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Mail, Lock, User as UserIcon, Building2, Sparkles, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

function formatErr(detail) {
  if (!detail) return 'Sign-up failed. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function Signup() {
  const { user, checkAuth } = useContext(AuthContext);
  const [form, setForm] = useState({ name: '', email: '', workspace_name: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) return setError('Password must be at least 8 characters.');
    if (form.password !== form.confirm) return setError('Passwords do not match.');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/signup`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        workspace_name: form.workspace_name.trim() || null,
      }, { withCredentials: true });
      if (r.data.session_token) setSessionToken(r.data.session_token);
      toast.success(`Welcome, ${r.data.user.name}! Your 7-day trial has started.`);
      await checkAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white flex flex-col">
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }}
      />
      <header className="relative z-30 backdrop-blur-xl bg-[#0a0f1e]/70 border-b border-teal-900/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo variant="icon" size={40} className="rounded-xl ring-1 ring-teal-500/30" />
            <div className="leading-tight">
              <div className="text-lg font-bold">FM Flexi Pro<span className="text-teal-400 text-xs align-top ml-0.5">™</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">by SKG Apps</div>
            </div>
          </Link>
          <Link to="/login" className="text-sm text-slate-300 hover:text-teal-400">Already have an account? Sign in →</Link>
        </div>
      </header>

      <main className="flex-1 relative z-10 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-teal-400" />
              <span className="text-xs font-medium text-teal-300">FREE 7-DAY TRIAL</span>
            </div>
            <h1 className="text-3xl font-bold">Start your workspace</h1>
            <p className="text-sm text-slate-400 mt-2">No credit card. No Google account needed. Set up in 30 seconds.</p>
          </div>

          <Card className="bg-slate-900/60 border-teal-900/40 backdrop-blur p-6 space-y-4">
            <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
              <div>
                <Label htmlFor="su-name" className="text-slate-200">Your name</Label>
                <div className="relative mt-1">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="su-name" data-testid="signup-name" value={form.name} onChange={upd('name')} required placeholder="Alex Al-Fahim" className="pl-10 bg-slate-950 border-slate-700 text-white" />
                </div>
              </div>
              <div>
                <Label htmlFor="su-email" className="text-slate-200">Work email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="su-email" data-testid="signup-email" type="email" value={form.email} onChange={upd('email')} required placeholder="you@company.com" className="pl-10 bg-slate-950 border-slate-700 text-white" autoComplete="email" />
                </div>
              </div>
              <div>
                <Label htmlFor="su-workspace" className="text-slate-200">Company / workspace name <span className="text-slate-500 text-xs">(optional)</span></Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="su-workspace" data-testid="signup-workspace" value={form.workspace_name} onChange={upd('workspace_name')} placeholder="Al Noor Realty" className="pl-10 bg-slate-950 border-slate-700 text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="su-pw" className="text-slate-200">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="su-pw" data-testid="signup-password" type="password" value={form.password} onChange={upd('password')} required placeholder="8+ characters" className="pl-10 bg-slate-950 border-slate-700 text-white" autoComplete="new-password" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="su-pw2" className="text-slate-200">Confirm</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="su-pw2" data-testid="signup-confirm" type="password" value={form.confirm} onChange={upd('confirm')} required placeholder="Repeat" className="pl-10 bg-slate-950 border-slate-700 text-white" autoComplete="new-password" />
                  </div>
                </div>
              </div>

              {error && (
                <div data-testid="signup-error" className="flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button data-testid="signup-submit" type="submit" disabled={submitting || !form.name || !form.email || !form.password || !form.confirm}
                      className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold">
                {submitting ? 'Creating your workspace…' : (<>Create workspace<ArrowRight className="w-4 h-4 ml-1" /></>)}
              </Button>
            </form>

            <ul className="text-xs text-slate-400 space-y-1 pt-2 border-t border-slate-800">
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />Instant access — no Google account required</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />7-day full-feature trial</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />Upgrade in-app when you&apos;re ready</li>
            </ul>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            By signing up you agree to our{' '}
            <Link to="/terms" className="text-teal-400 hover:underline">Terms</Link> &amp;{' '}
            <Link to="/privacy" className="text-teal-400 hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}
